import type {
  ChecklistSuggestionFailureReason,
  RunChecklistResolutionSummary,
} from '../shared/types';

export function batchChecklistSummaryLabel(
  summary: RunChecklistResolutionSummary,
  preview = false,
): string {
  const parts: string[] = [];
  if (summary.suggested > 0) {
    parts.push(
      preview
        ? `${summary.suggested} identities will be populated`
        : `${summary.suggested} identities populated`,
    );
  }
  if (summary.parallelChoices > 0) {
    parts.push(`${summary.parallelChoices} exact parallels need review`);
  }
  if (summary.noNumber > 0) parts.push(`${summary.noNumber} need card number`);
  if (summary.noMatch > 0) parts.push(`${summary.noMatch} not found`);
  if (summary.skippedReviewed > 0) {
    parts.push(`${summary.skippedReviewed} reviewed preserved`);
  }
  if (summary.truncated) {
    parts.push(preview ? 'first 500 included' : 'first 500 applied');
  }
  return parts.length > 0
    ? parts.join(' · ')
    : preview
      ? 'No unreviewed cards will change'
      : 'No unreviewed cards changed';
}

export function batchChecklistConfirmationLabel(summary: RunChecklistResolutionSummary): string {
  return `Apply this checklist to the batch? ${batchChecklistSummaryLabel(summary, true)}. Reviewed identities will not be changed.`;
}

export function checklistSuggestionActionLabel(
  suggesting: boolean,
  hasSuggestion: boolean,
): string {
  if (suggesting) return 'Analyzing card OCR…';
  return hasSuggestion ? 'Refresh card OCR suggestion' : 'Suggest from card OCR';
}

export function checklistSuggestionStatusLabel(
  status: string | undefined,
  configured: boolean,
  observation?: string,
  failureReason?: ChecklistSuggestionFailureReason,
  evidenceSource: 'ocr' | 'marketplace' = 'ocr',
): string {
  if (failureReason === 'ambiguous-roster') {
    return 'Card number or name appears in more than one possible checklist; choose the checklist manually';
  }
  if (failureReason === 'ambiguous-products') {
    return 'Card evidence matches multiple imported checklists; choose the checklist manually';
  }
  if (failureReason === 'below-confidence-threshold') {
    return 'Some product evidence was found, but it was not strong enough to select a checklist';
  }
  switch (status) {
    case 'no-ocr':
      return evidenceSource === 'marketplace'
        ? 'The eBay listing does not contain enough identity evidence'
        : 'OCR is not available for this card yet';
    case 'no-candidates':
      if (observation !== undefined) {
        if (evidenceSource === 'marketplace') {
          return configured
            ? `The eBay listing appears to show ${observation}, but it does not match another imported checklist; current selection unchanged`
            : `The eBay listing appears to show ${observation}, but a matching imported checklist was not found`;
        }
        return configured
          ? `This card's OCR appears to show ${observation}, but it does not match another imported checklist; current selection unchanged`
          : `Stored OCR resembles ${observation}, but TCVS could not confidently match it to an imported checklist`;
      }
      return configured
        ? 'This card did not match another imported checklist; current selection unchanged'
        : 'OCR did not match an imported checklist';
    case 'no-confident-match':
      if (evidenceSource === 'marketplace') {
        return configured
          ? 'The eBay listing could not confidently improve the current checklist; current selection unchanged'
          : 'The eBay listing matches multiple possible checklists; choose one manually';
      }
      return configured
        ? 'This card could not confidently improve the current checklist; current selection unchanged'
        : 'OCR found possible checklists but could not confidently choose one';
    case 'model-failed':
      return 'OCR checklist suggestion is temporarily unavailable';
    default:
      return 'No OCR checklist suggestion';
  }
}
